import type { ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';
import { ARTICLE_GROUND } from '../util/article-ground';
import {
    useArticleArrivalImage,
    useArticleDetailHeroStyle,
} from '../util/article-hero-view-transition';
import {
    DetailBreadcrumbBar,
    DetailCreditsRow,
    DetailSaveButton,
    type DetailItemStats,
} from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared/large-screen';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import type { EnquiryModalProfessional } from '~/modules/enquiry/hooks/use-enquiry-modal';
import type { EnquiryAudienceEnum } from 'generated/graphql';
import type { LoadedPageCtas } from '~/modules/enquiry/domain/page-ctas';
import type { TileVisitWebsiteConfig } from '~/modules/moodboards/components/TileActionPanel';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import type {
    ArticleRevampVariant,
    RevampCredit,
    RevampDownload,
    RevampHeaderCategory,
    RevampHeaderTheme,
} from '../type';
import { RevampModules } from './revamp-modules/RevampModules';
import { ArticleMagicIsland } from './ArticleMagicIsland';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { useHydrated } from 'remix-utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { ArticlePhotoViewerProvider } from './ArticlePhotoViewer';
import { RevampHeader } from './RevampHeader';
import { RevampBylineRow } from './RevampBylineRow';
import { RevampLogomark } from './RevampLogomark';
import { RevampOpeningBadge, RevampOpeningRow } from './RevampOpeningRow';
import { NextArticleTransition } from './NextArticleTransition';
import { RevampHeroImage, type RevampHeroImageProps } from './RevampHeroImage';
import { ArticleBannerPlayButton } from './ArticleBannerVideo';
import { RevampThemesCredits } from './RevampThemesCredits';
import { RevampShareLinks } from './RevampShareLinks';
import { ArticleHireSection } from './ArticleHireSection';
import { RevampDiscoverMore } from './RevampDiscoverMore';
import type {
    RevampDiscoverSection,
    RevampProfessional,
} from '../api/get-article-revamp-data.server';
import {
    RevampPremiumTitleSection,
    type RevampPremiumTitleSectionProps,
} from './RevampPremiumTitleSection';
import { RevampFlexiTitleSection } from './RevampFlexiTitleSection';
import { ArticlePageCtasProvider } from './ArticlePageCtasProvider';
import { useTracker } from '@archipro-website/tracker';
import { trackArticleViewProfileClick } from '~/modules/tracking/util/trackArticleProfessionalCta';

export interface ArticleRevampPageProps {
    variant: ArticleRevampVariant;
    header: {
        label?: string;
        title: string;
        categories: RevampHeaderCategory[];
        /** Reader-facing editorial themes; empty until an editor applies any. */
        themes: RevampHeaderTheme[];
    };
    teaser?: string;
    hero?: RevampHeroImageProps;
    credits: RevampCredit[];
    professional?: RevampProfessional;
    /** Who wrote it, for the meta row's "Written by" column: their name, and a face or initials for the disc beside it. */
    author?: { name: string; initials: string; avatar?: string };
    /** The professional's profile views and saves, for the trail's line. */
    stats?: DetailItemStats;
    discoverTiles: ArticleTileViewModel[];
    /**
     * A themed rail per tag the article carries. When there are none — an
     * article an editor has not tagged — `discoverTiles` draws the single
     * untitled rail instead.
     */
    discoverSections?: RevampDiscoverSection[];
    /** The article's guide files; the accordion row is omitted when there are none. */
    downloads?: RevampDownload[];
    /** Article the download event is attributed to. */
    articleID: number;
    /** The article's own path, shared by the Themes & Credits social buttons. */
    shareLink: string;
    /** Full professional behind the inline enquiry row. */
    enquiryProfessional?: EnquiryModalProfessional;
    /**
     * The loader's resolved enquiry and Book a meeting buttons. Null or absent
     * (Forms off, or the page builder) keeps the flat settings.
     */
    pageCtas?: LoadedPageCtas | null;
    /** The professional's flat `EnquiryAudience`, for the buttons' audience restriction. */
    enquiryAudience?: EnquiryAudienceEnum | null;
    /**
     * The practice's site, for the photo viewer's bottom-left "Visit site" —
     * the same action the article's tiles offer.
     */
    visitWebsite?: TileVisitWebsiteConfig | null;
    modules?: PageBuilderModuleType[];
    /** When true, truncate body modules and omit closing sections (auth banner is rendered by the route). */
    articleGatingEnabled?: boolean;
    /**
     * When set, the premium editorial title section replaces the standard
     * header + picture + summary stack (Figma 2479:73336).
     */
    premiumHero?: RevampPremiumTitleSectionProps;
    /**
     * When true (and a banner has been uploaded), the flexi split title
     * section replaces the same stack (Figma 2738:57368). It reuses the props
     * the standard stack already receives — banner, headline, categories and
     * teaser — so it needs no view model of its own. Premium takes precedence
     * if both tiers are somehow unlocked.
     */
    flexiTitle?: boolean;
    /**
     * Publish date for the byline. Premium renders it in its own credits row,
     * so this feeds the standard and flexi title sections.
     */
    publishDate?: string;
    /**
     * Frame the article for an author rather than a reader.
     *
     * The Business Manager page builder renders this page so the author edits
     * against what will publish. The masthead is the reader's, rendered inert:
     * every field in it — title, teaser, categories, themes, author — is
     * authored on the Article details tab, so leaving it live would offer a
     * second place to change them and swallow clicks meant for the editor.
     *
     * The recirculation below the body is dropped: it is a reader's, it needs
     * loader data the builder does not fetch, and it would push the editing
     * surface several screens down.
     */
    editorMode?: boolean;
    /**
     * The builder's live banner control, mounted in the masthead.
     *
     * It stands in for the hero picture on the standard masthead, and sits
     * above the premium and flexi ones, which draw their own. It has to appear
     * on every path, including an article that has no picture yet: the
     * landscape banner has no other editing surface in Business Manager, so a
     * masthead that omits it leaves the author no way to add or replace one.
     */
    heroSlot?: ReactNode;
    /** Rendered in place of the body modules — the builder's live module list. */
    modulesSlot?: ReactNode;
}

/**
 * Composes the revamped article page sections. The variant decides which
 * tier-gated sections render; standard shows the core editorial sections only.
 * Auth gating (GrowthBook `article-gating`) mirrors the legacy article tree:
 * body modules are truncated/blurred and post-body sections are omitted so the
 * sign-in banner can prompt unauthenticated readers.
 */
export const ArticleRevampPage = ({
    header,
    teaser,
    hero,
    credits,
    professional,
    author,
    stats,
    discoverTiles,
    discoverSections = [],
    modules,
    downloads = [],
    articleID,
    shareLink,
    enquiryProfessional,
    pageCtas = null,
    enquiryAudience,
    visitWebsite,
    articleGatingEnabled = false,
    premiumHero,
    flexiTitle = false,
    publishDate,
    editorMode = false,
    heroSlot,
    modulesSlot,
}: ArticleRevampPageProps) => {
    const hydrated = useHydrated();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    // A profile visit from the credits. The phone sends what the legacy
    // article's View profile link sent on a phone, which the dashboards
    // read; the desktop sends its Credits link's placement.
    const trackCreditsProfileClick = () => {
        if (!mobile) {
            trackArticleViewProfileClick(tracker, 'credits');
            return;
        }
        tracker.log('ProductProfileButtonClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    };
    // The next read the foot grows into: the first other article the reading
    // rails offer, with a link and a picture to grow. Chosen up here because
    // while the turn stands it is the end of the document — main's closing
    // padding stands down with it. Not on mobile: a flick's momentum keeps
    // scrolling through the input hold, so the turn lands the next page
    // already mid-scroll.
    const nextTile = mobile
        ? undefined
        : (discoverSections[0]?.tiles ?? discoverTiles).find(
              (tile) => tile.id !== articleID && tile.link && tile.image?.src,
          );
    // The title row carries the article's shelf first, then its subjects, then
    // who published it. All three draw as the same pill (Figma 2738:57343),
    // which is why the design shows a row of them where the page has
    // historically shown one category.
    //
    // The professional comes last and from the article's own relation rather
    // than from a theme: a studio is a party, not a subject, and tagging every
    // one of them would take the vocabulary from a few hundred terms to 1,378
    // and give each a landing page with one article on it. The byline above
    // names them; this makes the name a route to their profile.
    const subjectPills = [...header.categories, ...header.themes];
    const titlePills = [
        ...subjectPills,
        ...(professional ? [professional] : []),
    ];

    // One trail, two bars: the desktop one under the hero and the phone's at
    // the article's foot, so the two can never disagree on the route.
    const breadcrumbTrail = [
        { label: 'Home', to: '/' },
        { label: 'Articles', to: '/articles' },
        ...(header.categories[0]
            ? [
                  {
                      label: header.categories[0].title,
                      to: header.categories[0].link,
                  },
              ]
            : []),
    ];

    // The opening picture is the directory tile's counterpart: named while
    // a transition between the two is in flight, and painted with the tile's
    // rendition until its own has loaded.
    const heroStyle = useArticleDetailHeroStyle();
    const arrivalImage = useArticleArrivalImage();
    // The same subjects the title row shows, in the same order, minus the
    // publishing professional — Credits names them in the column right beside
    // this block. It used to show the editorial themes alone, so an article's
    // shelf appeared in the head and then vanished from the block that is
    // supposed to list what the article is filed under.
    const themePills = subjectPills;
    // The credits are the professional and the author together. Only the
    // professional carries a link, which is what separates the practice whose
    // work this is from the person who wrote about it.

    /**
     * The project page's meta row: the tags, who the article features, and
     * who wrote it. 64px under the intro on desktop; on the phone it closes
     * the article instead, after Like this Article?, so the opening runs
     * straight from the actions into the copy.
     */
    const renderCreditsRow = (className: string, trailing?: ReactNode) => (
        <div className={cn('w-full', className)}>
            <DetailCreditsRow
                // The article's one phone gutter is the prose's
                // 18px, over the row's shared 24.
                className={cn(ARTICLE_GROUND, 'max-md:px-4.5')}
                tags={titlePills.map((pill) => ({
                    label: pill.title,
                    to: pill.link,
                }))}
                columns={[
                    {
                        label: 'Featured Professionals',
                        entries: professional
                            ? [
                                  {
                                      name: professional.title,
                                      link: professional.link,
                                      onClick: trackCreditsProfileClick,
                                  },
                              ]
                            : [],
                    },
                    {
                        label: 'Written by',
                        trailing,
                        entries: author
                            ? [
                                  {
                                      name: author.name,
                                      avatar: {
                                          src: author.avatar || undefined,
                                          initials:
                                              author.initials ||
                                              author.name
                                                  .split(/\s+/)
                                                  .map((part) => part[0] ?? '')
                                                  .join('')
                                                  .slice(0, 2),
                                      },
                                  },
                              ]
                            : [],
                    },
                ]}
            />
        </div>
    );

    const renderTitleSection = () => {
        if (premiumHero) {
            return (
                // The banner is the masthead's ground rather than a picture
                // under it, so it is passed in and not rendered again below.
                <div className="-mt-10 flex w-full flex-col md:-mt-12">
                    {heroSlot && <div data-article-hero="">{heroSlot}</div>}
                    <RevampPremiumTitleSection
                        {...premiumHero}
                        tags={themePills}
                        backgroundImageUrl={hero?.imageUrl}
                        backgroundLibraryImageId={hero?.libraryImageId}
                    />
                </div>
            );
        }

        if (flexiTitle && hero) {
            return (
                // The design leaves 50px between this section and the first
                // body module rather than main's 180px, so claw the difference
                // back here instead of making the gap conditional.
                <div className="flex w-full flex-col md:-mb-[8.125rem] md:gap-[3.12rem]">
                    {heroSlot && <div data-article-hero="">{heroSlot}</div>}
                    <RevampLogomark />
                    <RevampFlexiTitleSection
                        imageUrl={hero.imageUrl}
                        alt={hero.alt}
                        title={header.title}
                        sponsoredBy={professional?.title}
                        categories={titlePills}
                        teaser={teaser}
                        publishDate={publishDate}
                    />
                </div>
            );
        }

        return (
            <>
                {/* Top to bottom: the headline, the picture the directory
                    tile grew into, the opening paragraph with the actions,
                    then the tags. The body follows the tags at the row's own 90px rather
                    than main's 180, so the difference is clawed back here. */}
                <div
                    className={cn(
                        'flex w-full flex-col items-center md:-mb-[5.625rem]',
                        // The claw-back is for the meta row's margin; on
                        // the phone the row is at the foot, so the body
                        // takes the page's own gap.
                        mobile ? 'max-md:mb-0' : '-mb-10',
                    )}
                >
                    {/* The masthead: the picture the directory tile grew
                        into, run from the top of the screen under the
                        transparent header, with the headline and the byline
                        in white over it on the pixels they hold on the
                        product page. On the phone they ride the photograph
                        too, at its foot on the scrim, the way the project
                        masthead carries its title — in flow above it they
                        painted white on the page's own white. */}
                    <div className="relative w-full">
                        <div
                            className={cn(
                                'w-full',
                                hero &&
                                    // Out of the flow over the picture, and
                                    // letting a press through to it: only
                                    // the headline's controls take clicks.
                                    'pointer-events-none absolute inset-x-0 z-10 max-md:bottom-0 max-md:pb-4 md:top-0 [&_a]:pointer-events-auto [&_button]:pointer-events-auto',
                            )}
                        >
                            {/* On the page's gutters and inside the same
                                width cap as the product title. Under the
                                transparent header nothing holds the band's
                                height, so the masthead keeps it — the band
                                plus the product section's own 0.6rem — and
                                the two headlines start on the same pixel. */}
                            <div
                                className={cn(
                                    'w-full px-4.5 md:px-[3rem]',
                                    hero
                                        ? 'md:pt-[24.225rem]'
                                        : 'md:pt-[0.6rem]',
                                )}
                            >
                                <div className={CONTENT_CAP_PADDED}>
                                    <RevampHeader
                                        title={header.title}
                                        tone={hero ? 'light' : 'ink'}
                                    />
                                </div>
                            </div>
                            {/* The product page's byline row, 48px under the
                                headline: Back, who it is by, and when. */}
                            <RevampBylineRow
                                professional={professional}
                                publishDate={publishDate}
                                fallbackLink="/articles"
                                theme={hero ? 'dark' : 'light'}
                            />
                            {/* Under Back, in the same column: the masthead's
                                film is the headline's business, not the
                                picture's. */}
                            {hero?.videoUrl ? (
                                <ArticleBannerPlayButton />
                            ) : null}
                        </div>
                        {(hero || heroSlot) && (
                            // The foot of the page before this one lands
                            // here: it puts this banner where the banner it
                            // grew was.
                            <div
                                data-article-hero=""
                                className={cn('w-full', ARTICLE_GROUND)}
                            >
                                {/* Edge to edge, square-cornered: a rounded
                                    edge against the viewport has nothing to
                                    sit on. The clip lives here, so the
                                    flight's snapshot keeps it. */}
                                <div
                                    style={heroStyle}
                                    className="w-full overflow-hidden"
                                >
                                    {heroSlot ??
                                        (hero && (
                                            <RevampHeroImage
                                                {...hero}
                                                fullBleed
                                                hideCaption
                                                underlaySrc={
                                                    arrivalImage ?? undefined
                                                }
                                                overlay={
                                                    // The directory masthead's grade:
                                                    // a flat tint that lifts contrast
                                                    // everywhere, and a scrim from the
                                                    // foot that carries the white type
                                                    // over a bright photograph.
                                                    <>
                                                        <div
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute inset-0 bg-[#131313]/[0.22]"
                                                        />
                                                        <div
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute inset-x-0 bottom-0 h-[36.875rem] bg-gradient-to-t from-[#131313]/[0.89] from-[6.207%] to-transparent to-[70.169%]"
                                                        />
                                                    </>
                                                }
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* The client's badge on the picture's lower edge, then
                        the product page's summary row: the opening paragraph
                        and the actions. */}
                    <RevampOpeningBadge
                        client={enquiryProfessional ? professional : undefined}
                    />
                    {/* The product page's category trail under the picture,
                        with the professional's profile views and saves on
                        its line. The picture's caption is not repeated here.
                        Desktop only: on the phone the trail leads the themes
                        and credits at the article's foot instead — here it
                        interrupted hero → lead — and the figures are not
                        restated on a page whose masthead already credits the
                        professional by name. */}
                    <div className="w-full max-md:hidden">
                        <DetailBreadcrumbBar
                            className={ARTICLE_GROUND}
                            stats={stats}
                            trail={breadcrumbTrail}
                        />
                    </div>
                    {/* The product page's 80px between its trail and its
                        summary row. */}
                    <div className="mt-10 w-full md:mt-[5rem]">
                        <RevampOpeningRow
                            teaser={teaser}
                            articleID={articleID}
                            articleTitle={header.title}
                            shareLink={shareLink}
                            heroImage={hero?.imageUrl}
                            client={
                                enquiryProfessional ? professional : undefined
                            }
                        />
                    </div>
                    {mobile ? null : renderCreditsRow('mt-10 md:mt-[4rem]')}
                </div>
            </>
        );
    };

    // The read-aloud island is the reader's furniture and it floats over the
    // foot of the viewport — in the builder that is on top of the editing
    // surface, offering to read the article to the person writing it.
    const withIsland = (children: ReactNode) =>
        editorMode ? (
            <>{children}</>
        ) : (
            <ArticleMagicIsland
                shareLink={shareLink}
                articleID={articleID}
                articleTitle={header.title}
                thumbnailUrl={hero?.imageUrl}
                intro={teaser}
            >
                {children}
            </ArticleMagicIsland>
        );

    const page = (
        <ArticlePhotoViewerProvider
            articleID={articleID}
            title={header.title}
            visitWebsite={visitWebsite}
        >
            {withIsland(
                <>
                    {/* The page ends on the Discover More rail, so the clearance above
                    the footer is the page's to keep rather than the last section's. */}
                    <main
                        className={cn(
                            // No stacking of its own. It briefly out-ranked the nav
                            // so the article could paint over it, which also put it
                            // above the floating islands — they sit at `z-fixed`,
                            // and the article is not meant to bury them. The nav is
                            // hidden once the banner reaches it instead, so there
                            // is nothing left to out-rank.
                            'relative',
                            // No ground of its own: it sits above the nav so the
                            // article can travel over it, and a background here
                            // covered the nav with a blank white band before the
                            // banner ever got there. The blocks that should hide
                            // the nav carry their own.
                            'flex w-full flex-col items-center gap-20 pt-0 md:gap-[11.25rem] md:pt-0',
                            // The closing padding stands down with the footer
                            // while the next-read turn is the document's end:
                            // the scroll clamp has to land on the grown banner,
                            // not on a band of ground past it.
                            nextTile ? 'pb-0' : 'pb-[6.25rem]',
                        )}
                    >
                        {editorMode ? (
                            // The reader's masthead, standing as context the
                            // author edits against. Inert and out of the
                            // accessibility tree: every field in it is authored
                            // on the Article details tab, so a live control here
                            // would be a second place to change one. The hero
                            // slot takes its pointer events back — the banner is
                            // the one thing in the masthead the builder owns.
                            <div
                                aria-hidden="true"
                                className="pointer-events-none w-full select-none [&_[data-article-hero]]:pointer-events-auto"
                            >
                                {renderTitleSection()}
                            </div>
                        ) : (
                            renderTitleSection()
                        )}
                        {/* The read-aloud bar reads its copy out of here: the body arrives
                    as page-builder modules, so the rendered prose is the only place
                    it exists as continuous text. */}
                        {modulesSlot ? (
                            <div data-article-body className="w-full">
                                {modulesSlot}
                            </div>
                        ) : null}
                        {!modulesSlot && modules && modules.length > 0 && (
                            <div data-article-body className="w-full">
                                <RevampModules
                                    modules={modules}
                                    professional={professional}
                                    openerImage={
                                        hero
                                            ? {
                                                  src: hero.imageUrl,
                                                  alt: hero.alt,
                                                  caption: hero.caption,
                                              }
                                            : undefined
                                    }
                                    articleGatingEnabled={articleGatingEnabled}
                                />
                            </div>
                        )}
                        {!articleGatingEnabled && !editorMode && (
                            <>
                                {professional && enquiryProfessional ? (
                                    <div className="w-full">
                                        <ArticleHireSection
                                            professional={professional}
                                            articleTitle={header.title}
                                            images={
                                                hero?.imageUrl
                                                    ? [hero.imageUrl]
                                                    : []
                                            }
                                        />
                                    </div>
                                ) : null}
                                {/* The phone's closing block, one flex child
                                so the page's 80px section gap falls around
                                it: a rule, the trail, then the meta row —
                                tags, who it features, who wrote it with the
                                share marks beside them — and the downloads
                                if there are any. The themes and credits the
                                desktop block sets again are already here.
                                px-4.5 is the article's one phone gutter. */}
                                {mobile ? (
                                    <div
                                        className={cn('w-full', ARTICLE_GROUND)}
                                    >
                                        <hr className="mx-4.5 my-0 h-px border-0 bg-black/10" />
                                        <DetailBreadcrumbBar
                                            className="max-md:px-4.5 max-md:pt-6 max-md:pb-6"
                                            trail={breadcrumbTrail}
                                        />
                                        {renderCreditsRow(
                                            '',
                                            <span className="flex shrink-0 items-center gap-[0.75rem]">
                                                <RevampShareLinks
                                                    shareLink={shareLink}
                                                    articleID={articleID}
                                                    articleTitle={header.title}
                                                />
                                                <DetailSaveButton
                                                    pinId={articleID}
                                                    pinType="ArticlePin"
                                                    className="size-[2.125rem]! border-solid"
                                                />
                                            </span>,
                                        )}
                                        <div className="mt-8 w-full">
                                            <RevampThemesCredits
                                                themes={themePills}
                                                credits={credits}
                                                professional={professional}
                                                downloads={downloads}
                                                articleID={articleID}
                                                articleTitle={header.title}
                                                shareLink={shareLink}
                                                enquiryProfessional={
                                                    enquiryProfessional
                                                }
                                                rowsOnly
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full">
                                        <RevampThemesCredits
                                            themes={themePills}
                                            credits={credits}
                                            professional={professional}
                                            downloads={downloads}
                                            articleID={articleID}
                                            articleTitle={header.title}
                                            shareLink={shareLink}
                                            enquiryProfessional={
                                                enquiryProfessional
                                            }
                                        />
                                    </div>
                                )}
                                {/* One rail per tag the article carries, each
                                listing what shares it. An article with no tags
                                keeps the single rail, named for its category
                                and drawn from the directory. */}
                                {discoverSections.length > 0 ? (
                                    discoverSections.map((section) => (
                                        <div
                                            className="w-full"
                                            key={section.theme.id}
                                        >
                                            <RevampDiscoverMore
                                                tiles={section.tiles}
                                                currentArticleID={articleID}
                                                theme={section.theme}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="w-full">
                                        <RevampDiscoverMore
                                            tiles={discoverTiles}
                                            currentArticleID={articleID}
                                            theme={themePills[0]}
                                        />
                                    </div>
                                )}
                                {/* The next read, growing into its own page as
                                the reader reaches the foot. */}
                                {nextTile && (
                                    <NextArticleTransition next={nextTile} />
                                )}
                            </>
                        )}
                    </main>
                    {/* Long page. Stacks above the chat dock on its own, as it
                    does on the product and project pages. Desktop only: the
                    island already owns the bottom of the mobile viewport. */}
                    {hydrated && !mobile && !editorMode ? <BackToTop /> : null}
                </>,
            )}
        </ArticlePhotoViewerProvider>
    );

    // The enquiry and Book a meeting buttons belong to the one client the
    // article is about; without one the page offers neither.
    return professional && enquiryProfessional ? (
        <ArticlePageCtasProvider
            loaded={pageCtas}
            enquiryAudience={enquiryAudience}
            professional={professional}
            enquiryProfessional={enquiryProfessional}
            articleID={articleID}
            articleTitle={header.title}
            heroImage={hero?.imageUrl}
        >
            {page}
        </ArticlePageCtasProvider>
    ) : (
        page
    );
};
